import type { WavetableCatalogEntry, WavetableRuntimeTable } from "./types";
import { WAVETABLE_FRAME_SIZE } from "./types";

const BASIC_FRAMES = 64;

function fillFrame(
  out: Float32Array,
  frameIndex: number,
  gen: (phase: number, t: number) => number,
): void {
  const t = BASIC_FRAMES <= 1 ? 0 : frameIndex / (BASIC_FRAMES - 1);
  const base = frameIndex * WAVETABLE_FRAME_SIZE;
  for (let i = 0; i < WAVETABLE_FRAME_SIZE; i++) {
    const phase = i / WAVETABLE_FRAME_SIZE;
    out[base + i] = gen(phase, t);
  }
}

function buildTable(id: string, gen: (phase: number, t: number) => number): WavetableRuntimeTable {
  const samples = new Float32Array(BASIC_FRAMES * WAVETABLE_FRAME_SIZE);
  for (let f = 0; f < BASIC_FRAMES; f++) {
    fillFrame(samples, f, gen);
  }
  return { id, frameSize: WAVETABLE_FRAME_SIZE, frameCount: BASIC_FRAMES, samples };
}

function polyBlep(t: number, dt: number): number {
  if (t < dt) {
    const x = t / dt;
    return x + x - x * x - 1;
  }
  if (t > 1 - dt) {
    const x = (t - 1) / dt;
    return x * x + x + x + 1;
  }
  return 0;
}

/** Serum-like starter morphs — baked at first access. */
export function createBasicWavetableCatalog(): WavetableCatalogEntry[] {
  return BASIC_TABLE_DEFS.map(def => ({
    id: def.id,
    name: def.name,
    source: "procedural" as const,
    group: "Basic",
    frameCount: BASIC_FRAMES,
  }));
}

type BasicDef = {
  id: string;
  name: string;
  gen: (phase: number, t: number) => number;
};

const BASIC_TABLE_DEFS: BasicDef[] = [
  {
    id: "basic/saw-square",
    name: "Saw → Square",
    gen: (phase, t) => {
      const dt = 1 / WAVETABLE_FRAME_SIZE;
      const saw = 2 * phase - 1 - polyBlep(phase, dt);
      const duty = 0.5;
      let sq = phase < duty ? 1 : -1;
      sq += polyBlep(phase, dt);
      sq -= polyBlep((phase - duty + 1) % 1, dt);
      return saw * (1 - t) + sq * t;
    },
  },
  {
    id: "basic/sine-saw",
    name: "Sine → Saw",
    gen: (phase, t) => {
      const dt = 1 / WAVETABLE_FRAME_SIZE;
      const sine = Math.sin(2 * Math.PI * phase);
      const saw = 2 * phase - 1 - polyBlep(phase, dt);
      return sine * (1 - t) + saw * t;
    },
  },
  {
    id: "basic/pwm",
    name: "PWM Sweep",
    gen: (phase, t) => {
      const dt = 1 / WAVETABLE_FRAME_SIZE;
      const duty = 0.05 + t * 0.9;
      let sq = phase < duty ? 1 : -1;
      sq += polyBlep(phase, dt);
      sq -= polyBlep((phase - duty + 1) % 1, dt);
      return sq * 0.9;
    },
  },
  {
    id: "basic/harmonics",
    name: "Harmonic Fade",
    gen: (phase, t) => {
      const n = 1 + Math.floor(t * 15);
      let sum = 0;
      let norm = 0;
      for (let h = 1; h <= n; h++) {
        const amp = 1 / h;
        sum += amp * Math.sin(2 * Math.PI * h * phase);
        norm += amp * amp;
      }
      return sum / Math.sqrt(Math.max(1e-6, norm));
    },
  },
  {
    id: "basic/mirror-fold",
    name: "Mirror Fold",
    gen: (phase, t) => {
      const sine = Math.sin(2 * Math.PI * phase);
      const drive = 1 + t * 6;
      let x = sine * drive;
      // triangle fold
      while (x > 1 || x < -1) {
        if (x > 1) {
          x = 2 - x;
        }
        if (x < -1) {
          x = -2 - x;
        }
      }
      return x;
    },
  },
  {
    id: "basic/metallic",
    name: "Metallic",
    gen: (phase, t) => {
      const a = Math.sin(2 * Math.PI * phase);
      const b = Math.sin(2 * Math.PI * phase * (2 + t * 5));
      const c = Math.sin(2 * Math.PI * phase * (3 + t * 7));
      return (a + 0.45 * b + 0.25 * c) / 1.7;
    },
  },
  {
    id: "basic/soft-bright",
    name: "Soft → Bright",
    gen: (phase, t) => {
      let sum = Math.sin(2 * Math.PI * phase);
      const odd = 1 + Math.floor(t * 10);
      for (let h = 3; h <= odd * 2 + 1; h += 2) {
        sum += (1 / h) * Math.sin(2 * Math.PI * h * phase) * t;
      }
      return sum * (0.7 + 0.3 * (1 - t));
    },
  },
  {
    id: "basic/tri-pulse",
    name: "Tri → Pulse",
    gen: (phase, t) => {
      const tri = 1 - 4 * Math.abs(phase - 0.5);
      const dt = 1 / WAVETABLE_FRAME_SIZE;
      const duty = 0.25;
      let pulse = phase < duty ? 1 : -1;
      pulse += polyBlep(phase, dt);
      pulse -= polyBlep((phase - duty + 1) % 1, dt);
      return tri * (1 - t) + pulse * t;
    },
  },
];

let cachedBasics: Map<string, WavetableRuntimeTable> | null = null;

export function getBasicWavetable(id: string): WavetableRuntimeTable | undefined {
  ensureBasicTables();
  return cachedBasics!.get(id);
}

export function ensureBasicTables(): Map<string, WavetableRuntimeTable> {
  if (cachedBasics) {
    return cachedBasics;
  }
  cachedBasics = new Map();
  for (const def of BASIC_TABLE_DEFS) {
    cachedBasics.set(def.id, buildTable(def.id, def.gen));
  }
  return cachedBasics;
}

export function listBasicWavetableIds(): string[] {
  return BASIC_TABLE_DEFS.map(d => d.id);
}
